<template>
  <inline-descriptor
    ref="inlineDescriptor"
    v-memo="[computedModelValue, computedSchema]"
    :id="id"
    editable
    :model-value="computedModelValue"
    :schema="computedSchema"
    :token-styles="tokenStyles"
    :separators="separators"
    :update-token="customUpdateToken"
    @chip-added="onChipAdded"
    @chip-removed="onChipRemoved"
    @chip-selected="onChipSelected"
  >
  </inline-descriptor>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
import { reactive } from 'vue';
import InlineDescriptor, { SchemaField, Token } from './InlineDescriptor.vue';

export interface Chip {
  id: string;
  value: string;
  dbId?: string;
}

@Component({
  emits: ['update:modelValue', 'chip-added', 'chip-removed', 'chip-selected'],
  components: { InlineDescriptor }
})
export default class ChipsInline extends Vue {
  @Prop({ type: String, required: true }) id!: string;
  @Prop({ type: Array, default: () => [] }) modelValue!: Chip[];
  @Prop({ type: Object, default: () => ({}) }) tokenStyles!: Record<string, string>;

  private separators = [' '];
  private chips = reactive<Chip[]>(Array.isArray(this.modelValue) ? this.modelValue : []);

  @Watch('modelValue')
  onModelValueChange(newValue: Chip[]) {
    this.chips = Array.isArray(newValue) ? newValue : [];
  }

  get computedSchema(): SchemaField[] {
    const schema: SchemaField[] = (this.chips || []).map(chip => ({
      key: chip.id,
      title: '',
      type: 'chip',
      prefix: '#',
      allowDelete: true,
      separator: this.separators[0],
      options: []
    }));
    schema.push({
      key: 'input',
      title: '',
      type: 'addtext',
      placeholder: 'Add tag',
      prefix: '#',
      allowDelete: false,
      separator: '',
      inputPattern: /^[a-zA-Z0-9\s]+$/,
      options: []
    });
    return schema;
  }

  get computedModelValue(): Record<string, Token[]> {
    const modelValue: Record<string, Token[]> = {};
    if (this.chips) {
      this.chips.forEach(chip => {
        if (chip?.id && chip?.value) {
          modelValue[chip.id] = [{
            text: chip.value,
            type: 'chip',
            id: chip.id,
            clickable: true
          }];
        }
      });
    }
    modelValue['input'] = [{ text: '', type: 'text', clickable: false }];
    return modelValue;
  }
  
  customUpdateToken(token: Token, field: SchemaField): Token {
    if (field.type === 'chip') {
      token.clickable = true;
      token.id = token.id || `chip_${Date.now()}`;
    }
    return token;
  }

  onChipAdded(data: { key: string; text: string; prefix?: string }) {
    const newChip: Chip = {
      id: data.key,
      value: data.text,
      dbId: `db_${Date.now()}`
    };
    this.chips = Array.isArray(this.chips) ? [...this.chips, newChip] : [newChip];
    this.$emit('update:modelValue', this.chips);
    this.$emit('chip-added', data);
    this.$nextTick(() => {
      this.$forceUpdate(); // Пересчитываем computedModelValue и computedSchema
    });
  }

  onChipRemoved(key: string) {
    this.chips = this.chips.filter(chip => chip.id !== key);
    this.$emit('update:modelValue', this.chips);
    this.$emit('chip-removed', key);
  }

  onChipSelected(data: { key: string; id?: string }) {
    this.$emit('chip-selected', data);
    this.$nextTick(() => {
      const inlineDescriptor = this.$refs.inlineDescriptor as any;
      const inputSection = inlineDescriptor.$el.querySelector('.section[data-key="input"]') as HTMLElement;
      if (inputSection) {
        inputSection.focus();
      }
    });
  }
}
</script>
<style scoped>
ul {
  list-style: none;
  padding: 0;
  margin: 0;
  min-width: 150px;
  max-width: fit-content;
}
li {
  padding: 8px;
  cursor: pointer;
}
li:hover {
  background: #f0f0f0;
}
</style>